import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiCheck, FiX, FiPlus, FiTrash2,
  FiFileText, FiMessageSquare, FiCheckSquare, FiClock,
  FiUpload, FiChevronDown, FiUser, FiDollarSign,
  FiCalendar, FiPhone, FiMail, FiSun, FiMoon,
} from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Card, Button, Badge, Select } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { calculateTargetPoints, parseOptionalNumber } from './targetPoints';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
const CARRIERS = [
  'F&G', 'Ethos', 'Ameritas', 'Corebridge', 'Foresters',
  'Mutual of Omaha', 'Quility', 'Banner', 'North American',
  'American National', 'Allianz', 'American Equity', 'Other',
];

const STATUSES = [
  { value: 'application_started', label: 'Application Started', color: 'bg-gray-500', light: 'bg-gray-100 text-gray-700', variant: 'default' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500', light: 'bg-blue-100 text-blue-700', variant: 'primary' },
  { value: 'pending_requirements', label: 'Pending Requirements', color: 'bg-yellow-500', light: 'bg-yellow-100 text-yellow-700', variant: 'warning' },
  { value: 'underwriting', label: 'Underwriting', color: 'bg-purple-500', light: 'bg-purple-100 text-purple-700', variant: 'purple' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500', light: 'bg-green-100 text-green-700', variant: 'success' },
  { value: 'advanced_paid', label: 'Advanced Paid', color: 'bg-emerald-600', light: 'bg-emerald-100 text-emerald-700', variant: 'teal' },
  { value: 'declined', label: 'Declined', color: 'bg-red-500', light: 'bg-red-100 text-red-700', variant: 'danger' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-400', light: 'bg-gray-100 text-gray-500', variant: 'default' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'comments', label: 'Comments' },
  { id: 'documents', label: 'Documents' },
  { id: 'history', label: 'History' },
];

const STATUS_LABELS = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));

function formatCurrency(val) {
  if (!val) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status, dark }) {
  const s = STATUSES.find((x) => x.value === status);
  if (!s) return null;
  if (dark) {
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${s.color} text-white`}>
        {s.label}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${s.light}`}>
      {s.label}
    </span>
  );
}

function FieldLabel({ children, dark }) {
  return (
    <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${dark ? 'text-gray-400' : 'text-gray-400'}`}>
      {children}
    </p>
  );
}

function FieldValue({ children, dark, mono }) {
  return (
    <p className={`text-sm ${mono ? 'font-mono' : ''} ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
      {children || '—'}
    </p>
  );
}

function EmptyState({ icon, title, subtitle, dark }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${dark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <SafeIcon icon={icon} className="w-5 h-5 text-gray-400" />
      </div>
      <p className={`text-sm font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

function StatusDropdown({ status, onUpdate, dark }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2">
        <StatusBadge status={status} dark={dark} />
        <FiChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            aria-label="Close status menu"
            onClick={() => setOpen(false)}
          />
          <div
            className={`absolute right-0 mt-2 w-52 rounded-lg shadow-lg overflow-hidden z-50 border ${
              dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            {STATUSES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => { onUpdate(s.value); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                  s.value === status
                    ? dark ? 'bg-gray-700' : 'bg-gray-50'
                    : dark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.color}`} />
                <span className={dark ? 'text-gray-200' : 'text-gray-700'}>{s.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SidebarSummary({ app, dark }) {
  const cardCls = dark ? 'p-4 bg-[#1a2736] border-gray-700' : 'p-4';
  const titleCls = `font-semibold mb-2 flex items-center gap-2 ${dark ? 'text-gray-100' : 'text-gray-900'}`;

  return (
    <>
      <Card className={cardCls}>
        <h3 className={titleCls}>
          <SafeIcon icon={FiFileText} className="text-primary" />
          Policy & Financials
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div>
            <FieldLabel dark={dark}>Carrier</FieldLabel>
            <FieldValue dark={dark}>{app.carrier}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Product</FieldLabel>
            <FieldValue dark={dark}>{app.product}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>App #</FieldLabel>
            <FieldValue dark={dark} mono>{app.application_number}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Policy #</FieldLabel>
            <FieldValue dark={dark} mono>{app.policy_number}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Policy Type</FieldLabel>
            <FieldValue dark={dark}>{app.policy_type}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Frequency</FieldLabel>
            <FieldValue dark={dark}>{app.premium_frequency}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Premium</FieldLabel>
            <FieldValue dark={dark}>{formatCurrency(app.premium) || '—'}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Face Amount</FieldLabel>
            <FieldValue dark={dark}>{formatCurrency(app.face_amount) || '—'}</FieldValue>
          </div>
          {app.is_split && (
            <div className="col-span-2">
              <FieldLabel dark={dark}>Split</FieldLabel>
              <FieldValue dark={dark}>
                {app.split_percent}% — {resolveDisplayName(app.split_agent_id, app.split_agent, app.split_agent_name) || '—'}
              </FieldValue>
            </div>
          )}
        </div>
      </Card>

      <Card className={cardCls}>
        <h3 className={titleCls}>
          <SafeIcon icon={FiCalendar} className="text-primary" />
          Timeline & Agent
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div>
            <FieldLabel dark={dark}>Created</FieldLabel>
            <FieldValue dark={dark}>{formatDate(app.created_at)}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Submitted</FieldLabel>
            <FieldValue dark={dark}>{formatDate(app.submitted_at)}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Approved</FieldLabel>
            <FieldValue dark={dark}>{formatDate(app.approved_at)}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Advanced Paid</FieldLabel>
            <FieldValue dark={dark}>{formatDate(app.issued_at)}</FieldValue>
          </div>
          <div>
            <FieldLabel dark={dark}>Primary Agent</FieldLabel>
            <FieldValue dark={dark}>{resolveDisplayName(app.agent_id, app.agent, app.agent_name)}</FieldValue>
          </div>
          {app.is_split && (
            <div>
              <FieldLabel dark={dark}>Split Agent</FieldLabel>
              <FieldValue dark={dark}>{resolveDisplayName(app.split_agent_id, app.split_agent, app.split_agent_name)}</FieldValue>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}

function OverviewTab({ app, onUpdate, dark }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [targetPointsOverridden, setTargetPointsOverridden] = useState(false);
  const { addToast } = useToast();

  const startEdit = () => {
    setForm({
      client_name: app.client_name || '',
      client_phone: app.client_phone || '',
      client_email: app.client_email || '',
      carrier: app.carrier || '',
      product: app.product || '',
      policy_type: app.policy_type || '',
      face_amount: app.face_amount ?? '',
      premium: app.premium ?? '',
      target_points: app.target_points ?? '',
      premium_frequency: app.premium_frequency || '',
      policy_number: app.policy_number || '',
      application_number: app.application_number || '',
      notes: app.notes || '',
      agent_name: app.agent_name || '',
      is_split: app.is_split || false,
      split_agent_name: app.split_agent_name || '',
      split_percent: app.split_percent ?? 0,
    });
    // Existing stored points count as an override so we don't silently overwrite on load
    setTargetPointsOverridden(app.target_points != null && app.target_points !== '');
    setEditing(true);
  };

  // Auto-calc target points until the user manually edits the field this session
  useEffect(() => {
    if (!editing || targetPointsOverridden) return;
    const computed = calculateTargetPoints({
      product: form.product,
      policy_type: form.policy_type,
      premium: form.premium,
      premium_frequency: form.premium_frequency,
    });
    const next = computed == null ? '' : String(computed);
    setForm((f) => (String(f.target_points ?? '') === next ? f : { ...f, target_points: next }));
  }, [
    editing,
    targetPointsOverridden,
    form.product,
    form.policy_type,
    form.premium,
    form.premium_frequency,
  ]);

  const save = async () => {
    try {
      const payload = {
        client_name: form.client_name || null,
        client_phone: form.client_phone || null,
        client_email: form.client_email || null,
        carrier: form.carrier || null,
        product: form.product || null,
        policy_type: form.policy_type || null,
        premium_frequency: form.premium_frequency || null,
        policy_number: form.policy_number || null,
        application_number: form.application_number || null,
        notes: form.notes || null,
        agent_name: form.agent_name || null,
        is_split: !!form.is_split,
        split_agent_name: form.is_split ? (form.split_agent_name || null) : null,
        face_amount: parseOptionalNumber(form.face_amount),
        premium: parseOptionalNumber(form.premium),
        target_points: parseOptionalNumber(form.target_points),
        split_percent: parseOptionalNumber(form.split_percent) ?? 0,
      };
      await onUpdate(payload);
      setEditing(false);
      addToast({ type: 'success', title: 'Application updated' });
    } catch {
      addToast({ type: 'error', title: 'Failed to save' });
    }
  };

  const inputCls = `w-full border rounded-md px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${
    dark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  }`;

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className={`text-sm font-semibold ${dark ? 'text-gray-200' : 'text-gray-900'}`}>
            Edit Application
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              <FiX className="w-3 h-3 mr-1.5" /> Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={save}>
              <FiCheck className="w-3 h-3 mr-1.5" /> Save
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'client_name', label: 'Client Name' },
            { key: 'client_phone', label: 'Phone' },
            { key: 'client_email', label: 'Email' },
            { key: 'application_number', label: 'Application #' },
            { key: 'policy_number', label: 'Policy #' },
            { key: 'policy_type', label: 'Policy Type' },
            { key: 'product', label: 'Product' },
            { key: 'agent_name', label: 'Agent Name' },
            { key: 'premium_frequency', label: 'Premium Frequency' },
          ].map(({ key, label }) => (
            <div key={key}>
              <FieldLabel dark={dark}>{label}</FieldLabel>
              <input
                className={inputCls}
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </div>
          ))}
          <div>
            <FieldLabel dark={dark}>Carrier</FieldLabel>
            <select
              className={inputCls}
              value={form.carrier}
              onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))}
            >
              <option value="">Select</option>
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel dark={dark}>Premium ($)</FieldLabel>
            <input
              type="number"
              className={inputCls}
              value={form.premium}
              onChange={(e) => setForm((f) => ({ ...f, premium: e.target.value }))}
            />
          </div>
          <div>
            <FieldLabel dark={dark}>Face Amount ($)</FieldLabel>
            <input
              type="number"
              className={inputCls}
              value={form.face_amount}
              onChange={(e) => setForm((f) => ({ ...f, face_amount: e.target.value }))}
            />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Target Points
            </p>
            <input
              type="number"
              className={inputCls}
              placeholder="Auto from premium / product"
              value={form.target_points}
              onChange={(e) => {
                setTargetPointsOverridden(true);
                setForm((f) => ({ ...f, target_points: e.target.value }));
              }}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <FieldLabel dark={dark}>Notes</FieldLabel>
            <textarea
              rows={3}
              className={`${inputCls} resize-none`}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </div>
      </div>
    );
  }

  const sectionCls = dark
    ? 'bg-gray-800/50 border border-gray-700 rounded-lg p-5'
    : 'bg-white border border-gray-100 rounded-lg p-5 shadow-sm';

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={startEdit}>
          <SafeIcon icon={FiEdit2} className="mr-2 w-3.5 h-3.5" />
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={sectionCls}>
          <h3 className={`font-semibold mb-4 flex items-center gap-2 ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
            <SafeIcon icon={FiDollarSign} className="text-primary" />
            Financials
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel dark={dark}>Target Points</FieldLabel>
              <FieldValue dark={dark}>
                {app.target_points
                  ? new Intl.NumberFormat('en-US').format(app.target_points) + ' pts'
                  : '—'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Premium</FieldLabel>
              <FieldValue dark={dark}>{formatCurrency(app.premium) || '—'}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Face Amount</FieldLabel>
              <FieldValue dark={dark}>{formatCurrency(app.face_amount) || '—'}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Frequency</FieldLabel>
              <FieldValue dark={dark}>{app.premium_frequency}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Split</FieldLabel>
              <FieldValue dark={dark}>
                {app.is_split ? `Yes — ${app.split_percent}%` : 'No'}
              </FieldValue>
            </div>
            {app.is_split && (
              <div>
                <FieldLabel dark={dark}>Split Agent</FieldLabel>
                <FieldValue dark={dark}>
                  {resolveDisplayName(app.split_agent_id, app.split_agent, app.split_agent_name)}
                </FieldValue>
              </div>
            )}
          </div>
        </div>

        <div className={sectionCls}>
          <h3 className={`font-semibold mb-4 flex items-center gap-2 ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
            <SafeIcon icon={FiCalendar} className="text-primary" />
            Timeline
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel dark={dark}>Created</FieldLabel>
              <FieldValue dark={dark}>{formatDate(app.created_at)}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Submitted</FieldLabel>
              <FieldValue dark={dark}>{formatDate(app.submitted_at)}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Approved</FieldLabel>
              <FieldValue dark={dark}>{formatDate(app.approved_at)}</FieldValue>
            </div>
            <div>
              <FieldLabel dark={dark}>Advanced Paid</FieldLabel>
              <FieldValue dark={dark}>{formatDate(app.issued_at)}</FieldValue>
            </div>
          </div>
        </div>

        <div className={sectionCls}>
          <h3 className={`font-semibold mb-4 flex items-center gap-2 ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
            <SafeIcon icon={FiUser} className="text-primary" />
            Agent
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel dark={dark}>Primary Agent</FieldLabel>
              <FieldValue dark={dark}>{resolveDisplayName(app.agent_id, app.agent, app.agent_name)}</FieldValue>
            </div>
            {app.is_split && (
              <div>
                <FieldLabel dark={dark}>Split Agent</FieldLabel>
                <FieldValue dark={dark}>
                  {resolveDisplayName(app.split_agent_id, app.split_agent, app.split_agent_name)}
                </FieldValue>
              </div>
            )}
          </div>
        </div>
      </div>

      {app.notes && (
        <div className={dark ? 'bg-amber-900/20 border border-amber-800 rounded-lg p-5' : 'bg-yellow-50 p-4 rounded-md border border-yellow-100'}>
          <p className={`font-semibold mb-1 text-sm ${dark ? 'text-amber-300' : 'text-yellow-800'}`}>Notes</p>
          <p className={`text-sm whitespace-pre-wrap ${dark ? 'text-gray-300' : 'text-gray-800'}`}>{app.notes}</p>
        </div>
      )}
    </div>
  );
}

function RequirementsTab({ applicationId, agencyId, dark }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const { addToast } = useToast();

  useEffect(() => {
    api.applications.requirements.getByApplication(applicationId)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [applicationId]);

  const add = async () => {
    if (!newTitle.trim()) return;
    try {
      const row = await api.applications.requirements.create({
        application_id: applicationId,
        agency_id: agencyId,
        title: newTitle.trim(),
        created_by: user.id,
      });
      setItems((p) => [...p, row]);
      setNewTitle('');
      setAdding(false);
    } catch {
      addToast({ type: 'error', title: 'Failed to add requirement' });
    }
  };

  const toggle = async (item) => {
    try {
      const updated = await api.applications.requirements.update(item.id, {
        completed: !item.completed,
        completed_at: !item.completed ? new Date().toISOString() : null,
      });
      setItems((p) => p.map((r) => (r.id === item.id ? updated : r)));
    } catch {
      addToast({ type: 'error', title: 'Failed to update' });
    }
  };

  const remove = async (id) => {
    try {
      await api.applications.requirements.delete(id);
      setItems((p) => p.filter((r) => r.id !== id));
    } catch {
      addToast({ type: 'error', title: 'Failed to delete' });
    }
  };

  const completed = items.filter((r) => r.completed).length;
  const inputCls = `flex-1 border rounded-md px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${
    dark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-semibold ${dark ? 'text-gray-200' : 'text-gray-900'}`}>Requirements</p>
          {items.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">{completed} of {items.length} completed</p>
          )}
        </div>
        <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
          <FiPlus className="w-3 h-3 mr-1.5" /> Add
        </Button>
      </div>

      {adding && (
        <div className="flex gap-2">
          <input
            autoFocus
            value={newTitle}
            className={inputCls}
            placeholder="Requirement title..."
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
              if (e.key === 'Escape') setAdding(false);
            }}
          />
          <Button size="sm" variant="primary" onClick={add}>Add</Button>
          <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
          <Spinner className="text-primary" />
          Loading requirements…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FiCheckSquare}
          title="No requirements yet"
          subtitle="Add items that need to be collected"
          dark={dark}
        />
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-3 rounded-lg border group ${
                dark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-100 shadow-sm'
              }`}
            >
              <button
                type="button"
                onClick={() => toggle(item)}
                className={`w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                  item.completed
                    ? 'bg-green-500 border-green-500'
                    : dark ? 'border-gray-600 hover:border-primary' : 'border-gray-300 hover:border-primary'
                }`}
              >
                {item.completed && <FiCheck className="w-3 h-3 text-white" />}
              </button>
              <span className={`flex-1 text-sm ${
                item.completed ? 'line-through text-gray-400' : dark ? 'text-gray-200' : 'text-gray-800'
              }`}>
                {item.title}
              </span>
              {item.completed && item.completed_at && (
                <span className="text-xs text-gray-400">{formatDate(item.completed_at)}</span>
              )}
              <button
                type="button"
                onClick={() => remove(item.id)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
              >
                <FiTrash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentsTab({ applicationId, agencyId, dark }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef(null);
  const { user } = useAuth();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();
  const agentUsers = useMemo(
    () => getActiveDirectoryUsers(directoryUsers),
    [directoryUsers],
  );

  useEffect(() => {
    api.applications.comments.getByApplication(applicationId)
      .then(setComments).finally(() => setLoading(false));
  }, [applicationId]);

  // Parse @mention trigger
  const handleBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);

    // Find if we're in a @mention context
    const cursor = e.target.selectionStart;
    const textToCursor = val.slice(0, cursor);
    const mentionMatch = textToCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const search = mentionMatch[1].toLowerCase();
      setMentionSearch(search);
      const filtered = agentUsers.filter(u =>
        u.id !== user.id &&
        u.full_name?.toLowerCase().includes(search)
      );
      setMentionUsers(filtered.slice(0, 6));
      setShowMentions(filtered.length > 0);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (mentionUser) => {
    const cursor = textareaRef.current?.selectionStart || body.length;
    const textToCursor = body.slice(0, cursor);
    const afterCursor = body.slice(cursor);
    const replaced = textToCursor.replace(/@(\w*)$/, `@${mentionUser.full_name} `);
    setBody(replaced + afterCursor);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (showMentions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => Math.min(i + 1, mentionUsers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (mentionUsers[mentionIndex]) {
          insertMention(mentionUsers[mentionIndex]);
        }
        return;
      }
      if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      submit();
    }
  };

  // Parse mentioned user IDs from comment body
  const parseMentionedIds = (text) => {
    const mentioned = [];
    const matches = text.match(/@([^@\n]+?)(?=\s|$|@)/g) || [];
    matches.forEach(match => {
      const name = match.slice(1).trim();
      const found = agentUsers.find(
        u => u.full_name?.toLowerCase() === name.toLowerCase()
      );
      if (found && !mentioned.includes(found.id)) {
        mentioned.push(found.id);
      }
    });
    return mentioned;
  };

  const submit = async () => {
    if (!body.trim()) return;
    try {
      setSubmitting(true);
      const mentioned_user_ids = parseMentionedIds(body);
      const row = await api.applications.comments.create({
        application_id: applicationId,
        agency_id: agencyId,
        body: body.trim(),
        created_by: user.id,
        author_name: user.full_name,
        mentioned_user_ids,
      });
      setComments(p => [...p, { ...row, mentioned_user_ids }]);
      setBody('');
    } catch {
      addToast({ type: 'error', message: 'Failed to post comment' });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.applications.comments.delete(id);
      setComments(p => p.filter(c => c.id !== id));
    } catch {
      addToast({ type: 'error', message: 'Failed to delete' });
    }
  };

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);

  const textareaCls = `w-full px-3 py-2 rounded-lg text-sm border 
    transition-colors focus:outline-none focus:border-[#00AAFF] resize-none
    ${dark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`;

  // Highlight @mentions in comment body
  const renderCommentBody = (text) => {
    const parts = text.split(/(@\S+(?:\s\S+)?)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const name = part.slice(1).trim();
        const isMentioned = agentUsers.some(
          u => u.full_name?.toLowerCase() === name.toLowerCase()
        );
        if (isMentioned) {
          return (
            <span key={i}
              className="text-[#00AAFF] font-medium bg-blue-50 
                         rounded px-0.5">
              {part}
            </span>
          );
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="space-y-4">
      <p className={`text-sm font-semibold 
        ${dark ? 'text-gray-200' : 'text-gray-900'}`}>
        Comments
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-[#00AAFF] 
                         border-t-transparent rounded-full animate-spin" />
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center 
                        py-12 text-center">
          <div className={`w-12 h-12 rounded-full flex items-center 
                          justify-center mb-3
            ${dark ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <FiMessageSquare className="w-5 h-5 text-gray-400" />
          </div>
          <p className={`text-sm font-medium 
            ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
            No comments yet
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Use @ to mention a teammate
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <div key={c.id}
              className={`p-4 rounded-xl border group
                ${dark
                  ? 'bg-gray-800/50 border-gray-700'
                  : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#1B4C87] 
                                  flex items-center justify-center 
                                  text-xs font-bold text-white flex-shrink-0">
                    {(c.author_name || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <span className={`text-xs font-semibold
                      ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {c.author_name || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      {formatDateTime(c.created_at)}
                    </span>
                  </div>
                </div>
                {(isAdmin || c.created_by === user?.id) && (
                  <button onClick={() => remove(c.id)}
                    className="opacity-0 group-hover:opacity-100 
                               text-gray-400 hover:text-red-500 
                               transition-all">
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className={`text-sm mt-2 ml-9 whitespace-pre-wrap
                ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                {renderCommentBody(c.body)}
              </p>
              {c.mentioned_user_ids?.length > 0 && (
                <div className="ml-9 mt-1 flex items-center gap-1">
                  <span className="text-xs text-gray-400">
                    Mentioned:
                  </span>
                  {c.mentioned_user_ids.map(uid => {
                    const u = agentUsers.find(a => a.id === uid);
                    return u ? (
                      <span key={uid}
                        className="text-xs text-[#00AAFF]">
                        @{u.full_name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Compose with @mention */}
      <div className={`border-t pt-4 
        ${dark ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={handleBodyChange}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment... Use @ to mention someone"
            rows={3}
            className={textareaCls}
          />

          {/* @mention dropdown */}
          {showMentions && mentionUsers.length > 0 && (
            <div className={`absolute bottom-full left-0 mb-1 w-64 
                            rounded-xl border shadow-xl overflow-hidden z-50
              ${dark
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'}`}>
              <p className={`px-3 py-2 text-xs border-b
                ${dark
                  ? 'text-gray-500 border-gray-700'
                  : 'text-gray-400 border-gray-100'}`}>
                Mention a teammate
              </p>
              {mentionUsers.map((u, i) => (
                <button
                  key={u.id}
                  onClick={() => insertMention(u)}
                  className={`w-full text-left px-3 py-2.5 flex 
                              items-center gap-3 transition-colors
                    ${i === mentionIndex
                      ? dark ? 'bg-gray-700' : 'bg-gray-50'
                      : dark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}>
                  <div className="w-6 h-6 rounded-full bg-[#1B4C87] 
                                  flex items-center justify-center 
                                  text-xs font-bold text-white flex-shrink-0">
                    {(u.full_name || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className={`text-sm font-medium
                      ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {u.full_name}
                    </p>
                    <p className="text-xs text-gray-400">{u.role}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-gray-400">
            @ to mention · Ctrl+Enter to submit
          </p>
          <button
            onClick={submit}
            disabled={!body.trim() || submitting}
            className="px-4 py-2 bg-[#00AAFF] hover:bg-blue-500 
                       disabled:opacity-40 disabled:cursor-not-allowed 
                       text-white text-xs font-medium rounded-lg 
                       transition-colors">
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ applicationId, dark }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.applications.documents.getByApplication(applicationId)
      .then(setDocs)
      .finally(() => setLoading(false));
  }, [applicationId]);

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
          <Spinner className="text-primary" />
          Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <EmptyState icon={FiUpload} title="No documents yet" subtitle="Document upload coming soon" dark={dark} />
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                dark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-100 shadow-sm'
              }`}
            >
              <SafeIcon icon={FiFileText} className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${dark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.name}</p>
                {doc.category && <p className="text-xs text-gray-400">{doc.category}</p>}
              </div>
              {doc.url && (
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ applicationId, dark }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.applications.getById(applicationId)
      .then((data) => setHistory(data?.status_history || []))
      .finally(() => setLoading(false));
  }, [applicationId]);

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
          <Spinner className="text-primary" />
          Loading history…
        </div>
      ) : history.length === 0 ? (
        <EmptyState icon={FiClock} title="No history yet" dark={dark} />
      ) : (
        <div className="relative">
          <div className={`absolute left-4 top-0 bottom-0 w-px ${dark ? 'bg-gray-700' : 'bg-gray-200'}`} />
          <div className="space-y-4">
            {[...history]
              .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
              .map((h, i) => (
                <div key={h.id || i} className="flex gap-4">
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 z-10 ${
                    dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    <SafeIcon icon={FiClock} className="w-3 h-3 text-gray-400" />
                  </div>
                  <div className="flex-1 bg-white p-4 rounded-lg border border-gray-100 shadow-sm dark:bg-gray-800/50 dark:border-gray-700">
                    <div className="flex items-center gap-2 flex-wrap">
                      {h.previous_status && (
                        <>
                          <span className="text-xs text-gray-400">
                            {STATUS_LABELS[h.previous_status] || h.previous_status}
                          </span>
                          <span className="text-xs text-gray-400">→</span>
                        </>
                      )}
                      <span className={`text-xs font-semibold ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {STATUS_LABELS[h.new_status] || h.new_status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {h.changed_by_name && <span className="text-xs text-gray-400">{h.changed_by_name}</span>}
                      <span className="text-xs text-gray-400">{formatDateTime(h.changed_at)}</span>
                    </div>
                    {h.notes && <p className="text-xs text-gray-400 mt-1">{h.notes}</p>}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LinkedLeadPanel({ app, onUpdate, dark }) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { addToast } = useToast();

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await api.applications.searchLeads(
          query,
          user.agency_id
        );
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, user.agency_id]);

  const handleLink = async (lead) => {
    try {
      await api.applications.linkLead(app.id, lead.id);
      onUpdate({
        lead_id: lead.id,
        lead: lead,
      });
      setSearching(false);
      setQuery('');
      setResults([]);
      addToast({ type: 'success', message: 'Lead linked' });
    } catch {
      addToast({ type: 'error', message: 'Failed to link lead' });
    }
  };

  const handleUnlink = async () => {
    try {
      await api.applications.unlinkLead(app.id);
      onUpdate({ lead_id: null, lead: null });
      addToast({ type: 'success', message: 'Lead unlinked' });
    } catch {
      addToast({ type: 'error', message: 'Failed to unlink lead' });
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg text-sm border 
    transition-colors focus:outline-none focus:border-[#00AAFF]
    ${dark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`;

  return (
    <div className={`rounded-xl border p-4
      ${dark
        ? 'bg-[#1a2736] border-gray-700'
        : 'bg-white border-gray-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-3">
        <FiUser className="w-4 h-4 text-[#00AAFF]" />
        <h3 className={`text-xs font-semibold uppercase tracking-wider
          ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
          Linked Lead
        </h3>
      </div>

      {app.lead_id && app.lead ? (
        <div>
          <div className={`flex items-center justify-between p-3 
            rounded-lg border mb-2
            ${dark
              ? 'bg-gray-800 border-gray-700'
              : 'bg-gray-50 border-gray-100'}`}>
            <div>
              <p className={`text-sm font-medium
                ${dark ? 'text-white' : 'text-gray-900'}`}>
                {app.lead.first_name} {app.lead.last_name}
              </p>
              {app.lead.phone && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {app.lead.phone}
                </p>
              )}
            </div>
            <a
              href={`/leads/${app.lead_id}`}
              onClick={e => { e.preventDefault(); window.location.href = `/leads/${app.lead_id}`; }}
              className="text-xs text-[#00AAFF] hover:underline mr-2"
            >
              View
            </a>
          </div>
          <button
            onClick={handleUnlink}
            className={`text-xs transition-colors
              ${dark
                ? 'text-gray-600 hover:text-red-400'
                : 'text-gray-400 hover:text-red-500'}`}>
            Unlink
          </button>
        </div>
      ) : (
        <div>
          {!searching ? (
            <button
              onClick={() => setSearching(true)}
              className={`flex items-center gap-1.5 text-xs transition-colors
                ${dark
                  ? 'text-gray-400 hover:text-white'
                  : 'text-gray-500 hover:text-gray-900'}`}>
              <FiPlus className="w-3 h-3" />
              Link to a lead
            </button>
          ) : (
            <div className="space-y-2">
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name, phone, email..."
                className={inputCls}
              />
              {loading && (
                <p className="text-xs text-gray-400 px-1">Searching...</p>
              )}
              {results.length > 0 && (
                <div className={`rounded-lg border overflow-hidden
                  ${dark
                    ? 'border-gray-700 bg-gray-800'
                    : 'border-gray-200 bg-white'}`}>
                  {results.map(lead => (
                    <button
                      key={lead.id}
                      onClick={() => handleLink(lead)}
                      className={`w-full text-left px-3 py-2.5 transition-colors
                        border-b last:border-0
                        ${dark
                          ? 'border-gray-700 hover:bg-gray-700'
                          : 'border-gray-100 hover:bg-gray-50'}`}>
                      <p className={`text-sm font-medium
                        ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {lead.first_name} {lead.last_name}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {lead.phone || lead.email || lead.lead_type}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {query.length >= 2 && !loading && results.length === 0 && (
                <p className="text-xs text-gray-500 px-1">No leads found</p>
              )}
              <button
                onClick={() => { setSearching(false); setQuery(''); setResults([]); }}
                className="text-xs text-gray-500 hover:text-gray-300">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  useUsersDirectory();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dark, setDark] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.applications.getById(id);
      setApp(data);
      // Mark any mentions as read when opening this application
      api.applications.comments.markMentionsRead(id).catch(() => {});
    } catch {
      addToast({ type: 'error', title: 'Failed to load application' });
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (updates) => {
    const updated = await api.applications.update(id, updates);
    setApp((prev) => ({ ...prev, ...updated }));
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.applications.updateStatus(id, newStatus);
      setApp((prev) => ({ ...prev, status: newStatus }));
      addToast({ type: 'success', title: 'Status updated' });
      load();
    } catch {
      addToast({ type: 'error', title: 'Failed to update status' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-gray-500 text-sm">Loading application…</p>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-gray-500 text-sm">Application not found.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/applications')}>
          Back to Applications
        </Button>
      </div>
    );
  }

  const headerCardCls = dark ? 'p-6 bg-[#1a2736] border-gray-700' : 'p-6';
  const mainCardCls = dark
    ? 'lg:col-span-2 flex flex-col h-[600px] bg-[#1a2736] border-gray-700'
    : 'lg:col-span-2 flex flex-col h-[600px]';
  const agentDisplayName = resolveDisplayName(app.agent_id, app.agent, app.agent_name);

  return (
    <div className={`space-y-6 pb-24 transition-colors ${dark ? 'bg-[#0F1923] -m-4 md:-m-8 p-4 md:p-8 min-h-full' : ''}`}>
      {/* Header card */}
      <Card className={headerCardCls}>
        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="flex items-start gap-5">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
              dark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'
            }`}>
              <SafeIcon icon={FiUser} className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <button
                  type="button"
                  onClick={() => navigate('/applications')}
                  title="Back to list"
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                </button>
                <h1 className={`text-2xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
                  {app.client_name}
                </h1>
              </div>

              <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-2 ${
                dark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {app.carrier && <span className="font-medium">{app.carrier}</span>}
                {app.product && (
                  <span className="flex items-center gap-1.5">
                    <span className="text-gray-400">·</span>
                    {app.product}
                  </span>
                )}
                {app.application_number && (
                  <span className="font-mono text-xs text-gray-500">#{app.application_number}</span>
                )}
              </div>

              <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-2 ${
                dark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {app.client_phone && (
                  <span className="flex items-center gap-1.5">
                    <SafeIcon icon={FiPhone} className="text-gray-400" />
                    {app.client_phone}
                  </span>
                )}
                {app.client_email && (
                  <span className="flex items-center gap-1.5">
                    <SafeIcon icon={FiMail} className="text-gray-400" />
                    {app.client_email}
                  </span>
                )}
                {agentDisplayName && (
                  <span className="flex items-center gap-1.5">
                    <SafeIcon icon={FiUser} className="text-gray-400" />
                    {agentDisplayName}
                  </span>
                )}
              </div>

              {(app.premium || app.face_amount || app.submitted_at) && (
                <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-2 ${
                  dark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {app.premium && (
                    <span className="flex items-center gap-1.5 font-medium">
                      <SafeIcon icon={FiDollarSign} className="text-primary" />
                      {formatCurrency(app.premium)} premium
                    </span>
                  )}
                  {app.face_amount && (
                    <span className="text-gray-500">{formatCurrency(app.face_amount)} coverage</span>
                  )}
                  {app.submitted_at && (
                    <span className="flex items-center gap-1.5 text-gray-500">
                      <SafeIcon icon={FiCalendar} className="text-gray-400" />
                      Submitted {formatDate(app.submitted_at)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-[200px] items-end">
            <StatusDropdown status={app.status} onUpdate={handleStatusUpdate} dark={dark} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDark((d) => !d)}
              className={dark ? 'border-gray-700 text-gray-400 hover:text-white' : ''}
            >
              {dark ? (
                <><FiSun className="w-3.5 h-3.5 mr-2" /> Light mode</>
              ) : (
                <><FiMoon className="w-3.5 h-3.5 mr-2" /> Dark mode</>
              )}
            </Button>
            <Button variant="outline" size="sm" className="w-full" onClick={() => setActiveTab('overview')}>
              <SafeIcon icon={FiEdit2} className="mr-2" />
              Edit Details
            </Button>
          </div>
        </div>
      </Card>

      {/* Main grid: sidebar + tabbed panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <LinkedLeadPanel
            app={app}
            onUpdate={(updates) => setApp(prev => ({ ...prev, ...updates }))}
            dark={dark}
          />
          <SidebarSummary app={app} dark={dark} />
        </div>

        <Card className={mainCardCls}>
          <div className={`flex items-center border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex flex-1 overflow-x-auto">
              {TABS.map(({ id: tab, label }) => (
                <button
                  key={tab}
                  type="button"
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : dark
                        ? 'border-transparent text-gray-500 hover:text-gray-300'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className={`flex-1 overflow-y-auto p-6 ${dark ? 'bg-gray-900/30' : 'bg-gray-50/50'}`}>
            {activeTab === 'overview' && (
              <OverviewTab app={app} onUpdate={handleUpdate} dark={dark} />
            )}
            {activeTab === 'requirements' && (
              <RequirementsTab applicationId={id} agencyId={app.agency_id} dark={dark} />
            )}
            {activeTab === 'comments' && (
              <CommentsTab applicationId={id} agencyId={app.agency_id} dark={dark} />
            )}
            {activeTab === 'documents' && (
              <DocumentsTab applicationId={id} dark={dark} />
            )}
            {activeTab === 'history' && (
              <HistoryTab applicationId={id} dark={dark} />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
